import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/* ──────────────────────────────────────────
   청명당 프로모션 팝업 (일반 이커머스형 랜딩 팝업)
   - 홈('/') 진입 시 1회 노출, "오늘 하루 보지 않기" 시 24시간 미노출
   - 이미지 클릭 → POPUP_LINK (가입 유도)
   - 가격표 이미지가 갱신되면 public/hightPopup06.jpeg 만 교체하면 됨
     (또는 아래 POPUP_IMAGE / POPUP_LINK 상수 변경)
────────────────────────────────────────── */

const DISMISS_KEY = 'saju2you_promo_2606_dismissed_until';
const DISMISS_HOURS = 24;            // "오늘 하루 보지 않기" 유지 시간
const SHOW_DELAY_MS = 700;           // 진입 후 노출 지연
const POPUP_IMAGE = `${process.env.PUBLIC_URL || ''}/hightPopup06.jpeg`;
const POPUP_LINK = '/register';      // 가입 시 무료 프로모 → 회원가입. 가격표로 보내려면 '/charge'
const POPUP_ALT = '청명당 오픈기념 — 가입 시 프리미엄 사주 2회 무료';

function hiddenForToday() {
  try {
    const until = localStorage.getItem(DISMISS_KEY);
    return !!until && Date.now() < Number(until);
  } catch {
    return false;
  }
}

export default function PromoPopup() {
  const location = useLocation();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  // 홈 진입 시에만, 미노출 기간이 아니면 지연 후 노출
  useEffect(() => {
    if (location.pathname !== '/') {
      setShow(false);
      return undefined;
    }
    if (hiddenForToday()) return undefined;
    const t = setTimeout(() => setShow(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const close = useCallback(() => setShow(false), []);

  const hideToday = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_HOURS * 3600000));
    } catch {
      /* localStorage 비활성 환경은 그냥 닫기만 */
    }
    setShow(false);
  }, []);

  const goLink = useCallback(() => {
    setShow(false);
    navigate(POPUP_LINK);
  }, [navigate]);

  // Esc 닫기 + 바디 스크롤 잠금
  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [show, close]);

  if (!show) return null;

  return (
    <div role="dialog" aria-modal="true" aria-label={POPUP_ALT} onClick={close} style={S.overlay}>
      <div onClick={(e) => e.stopPropagation()} style={S.card}>
        <button type="button" onClick={close} aria-label="팝업 닫기" style={S.closeBtn}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <button type="button" onClick={goLink} aria-label={POPUP_ALT} style={S.imgBtn}>
          <img src={POPUP_IMAGE} alt={POPUP_ALT} style={S.img} />
        </button>

        <div style={S.footer}>
          <button type="button" onClick={hideToday} style={S.footBtn}>오늘 하루 보지 않기</button>
          <button type="button" onClick={close} style={{ ...S.footBtn, fontWeight: 700, borderLeft: '1px solid #eee' }}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(20,16,12,0.62)', zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
  },
  card: {
    position: 'relative', width: 'min(420px, 92vw)', maxHeight: '92vh',
    display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: '16px',
    overflow: 'hidden', boxShadow: '0 24px 70px -12px rgba(0,0,0,0.5)',
  },
  closeBtn: {
    position: 'absolute', top: 8, right: 8, width: 34, height: 34, display: 'grid',
    placeItems: 'center', borderRadius: '50%', border: 'none',
    background: 'rgba(0,0,0,0.42)', color: '#fff', cursor: 'pointer', zIndex: 2,
  },
  imgBtn: {
    display: 'block', flex: '1 1 auto', minHeight: 0, overflowY: 'auto',
    padding: 0, border: 'none', background: 'none', cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
  },
  img: { display: 'block', width: '100%', height: 'auto' },
  footer: { display: 'flex', flex: '0 0 auto', borderTop: '1px solid #eee' },
  footBtn: {
    flex: 1, padding: '13px 8px', border: 'none', background: '#fff',
    color: '#333', fontSize: '14px', cursor: 'pointer',
  },
};
